import {useState, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {get} from '@cullylarson/f'
import {sendEvent} from '@@client/lib/gtm'
import {preventDefault} from '@@client/lib/events'
import Layout from '@@client/app/Layout'
import {useTokenCache, useConfig} from '@@client/lib/hooks'
import GeneralPage, {Header, Content} from '@@client/app/GeneralPage'
import Messages from '@@client/components/Messages'
import FormText from '@@client/components/FormText'
import FormTextArea from '@@client/components/FormTextArea'
import FormPhone from '@@client/components/FormPhone'
import FormDate from '@@client/components/FormDate'
import FormEmail from '@@client/components/FormEmail'
import FormCheckboxArray from '@@client/components/FormCheckboxArray'
import FormSmsConsentCheckbox from '@@client/components/FormSmsConsentCheckbox'
import loadLocationList from '@@client/location/loadLocationList'
import {sendHiringMessage} from '@@client/message/actions'
import {addNotificationSuccess} from '@@client/notification/actions'

const HiringForm = ({errors, paramErrors, disabled}) => {
    loadLocationList()

    const ref = useRef(null)
    const dispatch = useDispatch()
    const tokenCache = useTokenCache()
    const locationStore = useSelector(state => state.location)
    const authSession = tokenCache.getAuth()

    const [contactName, setContactName] = useState(authSession.account?.name || '')
    const [contactEmail, setContactEmail] = useState(authSession.account?.email || '')
    const [contactPhone, setContactPhone] = useState(authSession.account?.phone || '')
    const [storeNames, setStoreNames] = useState([])
    const [positions, setPositions] = useState([])
    const [startDate, setStartDate] = useState(new Date())
    const [message, setMessage] = useState('')
    const [smsConsent, setSmsConsent] = useState(false)

    const handleParam = setFn => e => (e && e.target) ? setFn(e.target.value) : setFn(e)
    const handleParamCheckbox = setValue => e => (e && e.target && e.target.checked) ? setValue(true) : setValue(false)

    const handleSubmit = preventDefault(() => {
        if(disabled) return

        dispatch(sendHiringMessage(tokenCache, {
            contactName,
            contactEmail,
            contactPhone,
            storeNames,
            positions,
            startDate,
            message,
            smsConsent,
        }))
            .then(success => {
                if(success) {
                    sendEvent('hiring-form')

                    ref.current.scrollIntoView(true)

                    dispatch(addNotificationSuccess("Your message was sent. We'll get back to you soon."))

                    setStoreNames([])
                    setPositions([])
                    setMessage('')
                }
            })
    })

    const storeOptions = locationStore.list.entities.map(x => {
        const label = x.name || `${x.city}, ${x.state}`
        const value = label

        return {label, value}
    })

    const idPrefix = 'hiring'

    return (
        <>
            <Messages error={errors} />

            <form onSubmit={handleSubmit} ref={ref}>
                <div className='field-sets'>
                    <div>
                        <FormText
                            idPrefix={idPrefix}
                            name='contactName'
                            title='Full Name'
                            value={contactName}
                            onChange={handleParam(setContactName)}
                            errors={get('contactName', [], paramErrors)}
                            disabled={disabled}
                            required
                            autoComplete='name'
                        />
                        <FormEmail
                            idPrefix={idPrefix}
                            name='contactEmail'
                            title='Email'
                            value={contactEmail}
                            onChange={handleParam(setContactEmail)}
                            errors={get('contactName', [], paramErrors)}
                            disabled={disabled}
                            required
                            autoComplete='email'
                        />
                        <FormPhone
                            idPrefix={idPrefix}
                            name='contactPhone'
                            title='Phone'
                            value={contactPhone}
                            onChange={handleParam(setContactPhone)}
                            disabled={disabled}
                            errors={get('contactPhone', [], paramErrors)}
                            autoComplete='tel'
                            required
                        />
                        <FormCheckboxArray
                            kind='checkbox'
                            idPrefix={idPrefix}
                            name='positions'
                            title='Position(s) you are interested in (multiple choice)'
                            onChange={setPositions}
                            selectedValues={positions}
                            options={[
                                {label: 'Sales', value: 'Sales'},
                                {label: 'Management', value: 'Management'},
                                {label: 'QC', value: 'QC'},
                                {label: 'Shipping', value: 'Shipping'},
                                {label: 'Inventory', value: 'Inventory'},
                                {label: 'Driver', value: 'Driver'},
                                {label: 'Parts Puller/Dismantling', value: 'Parts Puller/Dismantling'},
                                {label: 'Office', value: 'Office'},
                                {label: 'Other', value: 'Other'},
                            ]}
                            disabled={disabled}
                        />
                        <FormTextArea
                            idPrefix={idPrefix}
                            title='Let us know why you chose and how you would best fit this position(s).'
                            name='message'
                            value={message}
                            onChange={handleParam(setMessage)}
                            errors={get('message', [], paramErrors)}
                            disabled={disabled}
                            required
                        />
                        <FormDate
                            idPrefix={idPrefix}
                            name='startDate'
                            title='When can you start?'
                            value={startDate}
                            onChange={setStartDate}
                            disabled={disabled}
                            errors={get('startDate', [], paramErrors)}
                            required
                        />
                        {
                            !storeOptions.length
                                ? null
                                : (
                                    <FormCheckboxArray
                                        kind='checkbox'
                                        idPrefix={idPrefix}
                                        title='Location(s) you would be interested at starting at (multiple choice)'
                                        name='storeNames'
                                        selectedValues={storeNames}
                                        options={storeOptions}
                                        onChange={setStoreNames}
                                        errors={get('storeNames', [], paramErrors)}
                                    />
                                )
                        }

                        <div className='signup'>
                            <FormSmsConsentCheckbox
                                idPrefix={idPrefix}
                                name='smsConsent'
                                isChecked={smsConsent}
                                onChange={handleParamCheckbox(setSmsConsent)}
                                errors={get('smsConsent', [], paramErrors)}
                                disabled={disabled}
                            />
                        </div>

                        <div className='actions'>
                            <button className='btn btn--yellow' type='submit' disabled={disabled}>Send</button>
                        </div>
                    </div>
                </div>
            </form>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .field-sets {
                        @media(--md) {
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            grid-gap: 30px;
                        }
                    }

                    form :global(textarea) {
                        height: 200px;
                    }
                `}
            </style>
        </>
    )
}

export default function Page() {
    const config = useConfig()
    const hiringStore = useSelector(state => state.message.hiring)

    if(!config.hiring.enabled) {
        return null
    }

    return (
        <Layout title='Apply with B&R'>
            <GeneralPage>
                <Header title='B&R Auto Wrecking is hiring!'>
                    Looking for a career change?  Let B&amp;R know where you want to start!
                </Header>
                <Content>
                    <h2>Apply Now</h2>
                    <HiringForm errors={hiringStore.errors} paramErrors={hiringStore.paramErrors} disabled={hiringStore.doing} />
                </Content>
            </GeneralPage>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    h2 {
                        color: $c--blue--dark;
                    }
                `}
            </style>
        </Layout>
    )
}
